import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ChatsListItem from '../../Chat/ChatListItem';
import TaskListItem from '../../Tasks/TaskListItem';
import useChatConnections from 'hooks/redux/useChatConnections';
import useContactLogForm from 'hooks/useContactLogForm';
import useContact from 'hooks/redux/useContact';
import useContacts from 'hooks/redux/useContacts';
import useMedia from 'hooks/useMedia';
import { taskMissed } from 'store/tasks/tasks.actions';
import useTaskConnections from 'hooks/redux/useTaskConnections';
import { ClickableLineWithArrow } from 'components';
import styles from './ContactList.module.scss';
import { IChat } from 'store/chat/chat.state';
import { ITask } from 'store/tasks/tasks.state';
import classNames from 'classnames';
import { getThreadsNewMessageCounts } from '../../../services/api/api.thread';
import useInterval from '../../../hooks/useInterval';

interface Props { }

interface ExtendedITask extends ITask {
    contactType: string;
}

interface ExtendedIChat extends IChat {
    contactType: string;
}

interface ThreadIdWithNewMessageCount {
    threadID: string;
    newMessageCount?: number;
}

function expandWithTypeField<Type>(
    contactType: string,
    contactList: Type[]
): Type[] {
    return contactList.length > 0
        ? contactList.map((contact) => {
            return { ...contact, contactType };
        })
        : contactList;
}

const ContactList: React.FC<Props> = () => {
    //needs for loading forms if reload page not on contactLog page
    //uses for status dropdown validation
    useContactLogForm();

    const dispatch = useDispatch();

    const itemsAmountInHeader = 2;
    const chatsList = expandWithTypeField('chat', useChatConnections()) as ExtendedIChat[];
    const tasksList = expandWithTypeField('task', useTaskConnections()) as ExtendedITask[];
    const [threadsNewMessageCounts, setThreadsNewMessageCounts] = useState<ThreadIdWithNewMessageCount[]>([]);

    const [showPopup, setShowPopup] = useState(false);

    const [isMobile] = useMedia();

    const contact = useContact();

    const contacts = useContacts();

    const getTasksAndChatsLength = () => chatsList.length + tasksList.length;

    const refreshThreadsNewMessageCount = async () => {
        if (tasksList.length > 1) {
            const taskListThreadIds = tasksList
                .map(task => task.threadID)
                .filter(Boolean) as string[];
            if (taskListThreadIds.length) {
                setThreadsNewMessageCounts(await getThreadsNewMessageCounts(taskListThreadIds));
            }
        }
    };

    useInterval(() => {
        refreshThreadsNewMessageCount();
    }, 30000);

    useEffect(() => {
        setTimeout(() => refreshThreadsNewMessageCount(), 500);
    }, []);

    useEffect(() => {
        if (tasksList.find(task => task.status === 'connecting')) {
            refreshThreadsNewMessageCount();
        }

        const rejectedTasks = tasksList.filter(({ status }) => status === 'rejected');
        rejectedTasks.forEach(({ id }) => {
            // Like the contact.onRefresh callback in the listeners.contact.ts middleware. Marks rejected tasks as missed.
            dispatch(taskMissed(id));
        });
    }, [tasksList.length]);

    useEffect(() => {
        // When we select a contact, we should remove the newMessageCount
        if (contact && contact.attributes['sa-threadID']) {
            const threadsNewMessageCountsFiltered = threadsNewMessageCounts.filter(
                t => t.threadID !== contact.attributes['sa-threadID']
            );
            if (threadsNewMessageCountsFiltered.length !== threadsNewMessageCounts.length) {
                setThreadsNewMessageCounts(threadsNewMessageCountsFiltered);
            }
        }
    }, [contact?.ID]);

    const allItems = useMemo(() => {
        const tasksWithNewMessageCounts = tasksList.map(task => {
            const newMessageCount = threadsNewMessageCounts.find(
                threadAndNewMessageCount => threadAndNewMessageCount.threadID === task.threadID
            )?.newMessageCount;
            return newMessageCount ? { ...task, newMessageCount } : task;
        });
        return [...chatsList, ...tasksWithNewMessageCounts];
    }, [chatsList, tasksList, threadsNewMessageCounts]);

    const getItemsForHeader = () => allItems.slice(0, itemsAmountInHeader);

    const getItemsForPopup = () =>
        allItems.slice(itemsAmountInHeader, allItems.length);

    const findChannelAttributes = (contactID: string, attribute: string) => {
        return contacts.find(contact => contact.ID === contactID)?.attributes[attribute];
    }
    
    const renderItem = (item: ExtendedITask | ExtendedIChat, index: number) => {
        if (item.contactType === 'chat') {
            return (
                <ChatsListItem
                    selected={item.id === contact?.ID}
                    chat={item as IChat}
                    index={index + 1}
                    channel={findChannelAttributes(item.id, 'sa-social-media-platform')}
                    subChannel={findChannelAttributes(item.id, 'sa-sub-channel')}
                />
            );
        }

        if (item.contactType === 'task') {
            return (
                <TaskListItem
                    selected={item.id === contact?.ID}
                    task={item as ITask}
                    index={chatsList.length + index + 1}
                />
            );
        }
    };

    const renderItemsList = (list: (ExtendedITask | ExtendedIChat)[]) => {
        return (
            <ol className={styles.contactList} data-test-id="contactList">
                {list.map((item, i) => (
                    <li key={item.id}>{renderItem(item, i)}</li>
                ))}
            </ol>
        );
    };

    const listWrapperClassName = classNames(styles.listWrapper, {
        [styles['listWrapper--withPadding']]: getTasksAndChatsLength() <= itemsAmountInHeader
    })

    const renderList = () => {
        if (isMobile) {
            const itemsForHeader = getItemsForHeader();
            const itemsForPopup = getItemsForPopup();

            return (
                <>
                    <div className={listWrapperClassName}>
                        {renderItemsList(itemsForHeader)}
                    </div>

                    <div className={styles.expandableSection}>
                        {getTasksAndChatsLength() > itemsAmountInHeader && (
                            <ClickableLineWithArrow
                                className={styles.line}
                                arrowClassName={styles.arrow}
                                onClick={() => setShowPopup(!showPopup)}
                            />
                        )}
                    </div>

                    {showPopup && itemsForPopup.length > 0 && (
                        <div className={styles.Popup}>
                            <div className={styles.Popup_wrapper}>
                                <div className={styles.Popup_content}>
                                    {renderItemsList(itemsForPopup)}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            );
        }

        return renderItemsList(allItems);
    };

    return <>{allItems.length > 0 && isMobile !== undefined && renderList()}</>;
};

export default ContactList;
